import { useMemo } from 'react'
import { useStore } from '@/store'
import { countsForWorldHops } from '@/hooks/useViewCounts'

/**
 * Compact World KPI strip — driven by worldFlowResult hops (not empty searchResult).
 * Totals share the exact counting semantics of the Hex view (useViewCounts).
 */
export default function MetricRow() {
  const flow = useStore((s) => s.worldFlowResult)

  const stats = useMemo(() => {
    const hops = flow?.hops ?? []
    const clients = hops.filter((h) => h.type === 'city')
    let crit = 0
    let warn = 0
    for (const h of clients.length ? clients : hops) {
      if (h.avgSeverity === 0) crit += 1
      else if (h.avgSeverity === 1) warn += 1
    }
    const counts = countsForWorldHops(hops)
    return { crit, warn, users: counts.usersTotal, streams: counts.totalStreams }
  }, [flow])

  const cell = (label: string, value: string | number, tone?: 'crit' | 'warn') => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 10, color: 'var(--t-2)', whiteSpace: 'nowrap' }}>{label}</span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: tone === 'crit' ? 'var(--crit)' : tone === 'warn' ? 'var(--warn)' : 'var(--t-0)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  )

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        flexWrap: 'wrap',
        minHeight: 28,
        padding: '4px 2px',
        borderBottom: '0.5px solid var(--bd-0)',
      }}
    >
      {cell('Critical', stats.crit, stats.crit > 0 ? 'crit' : undefined)}
      {cell('Warnings', stats.warn, stats.warn > 0 ? 'warn' : undefined)}
      {cell('Streams', stats.streams)}
      {cell('Users', Number.isFinite(stats.users) ? stats.users : '—')}
    </div>
  )
}
